import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Line } from "react-chartjs-2";
import {
  Form,
  Button,
  Card,
  theme,
  Input,
  Grid,
  Select,
} from "antd";

import BrandColors from "@brand-colors";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { usePregnancyWeightSrv } from "./usePregnancyWeightSrv";
import useToasterMessage from "helpers/useToasterMessage";
import "./PregnancyWeightCalculator.css"
import { handleErrorNavigation } from "pages/ErrorPage/ErrorHandler";
import { useNavigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import whitelabelConfig from "@whitelabel-config";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const PregnancyWeightChart = ({ }) => {


  const options = {
    interaction: {
      mode: 'index',
      intersect: false,
    },
  }

  return (
    <div>
      <Line
        data={chartData}
        options={options}
      />
    </div>
  );
};

let chartData: any = {};
let chartDesignConfig: any = {};
let chartOptions: any = {};
let chartColors = {
  red: "rgb(255, 99, 132)",
  orange: "rgb(255, 159, 64)",
  yellow: "rgb(255, 205, 86)",
  green: "rgb(75, 192, 192)",
  blue: "rgba(54, 162, 235,0.25)",
  purple: "rgb(153, 102, 255)",
  grey: "rgb(201, 203, 207)",
};

const PregnancyWeightCalculator = () => {
  const {
    getPregnancyToolData,
    UpdatePregnancyWeight,
    GetPatientMobileSettings,
  } = usePregnancyWeightSrv();
  const [pregnancyData, setPregnancyData] = useState(null);
  const [healthPayload, setPayload] = useState({});
  const [weeksPregnant, setWeeksPregnant] = useState("");
  const [currentWeight, setCurrentWeight] = useState("");
  const [form] = Form.useForm();
  const { Option } = Select;
  const { useToken } = theme;
  const { useBreakpoint } = Grid;
  const { token } = useToken();
  const screens = useBreakpoint();
  const { UseToaster } = useToasterMessage();
  const [isHover, setIsHover] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleMouseEnter = () => {
    setIsHover(true);
  };
  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const styles = {
    container: {
      margin: "0 auto",
      padding: screens.md
        ? `${token.paddingXL}px`
        : `${token.sizeXXL}px ${token.padding}px`,
      width: "380px",
    },
    footer: {
      marginTop: token.marginLG,
      textAlign: "center",
      width: "100%",
    },
    forgotPassword: {
      float: "right",
    },
    header: {
      marginBottom: token.marginXL,
    },
    section: {
      alignItems: "center",
      backgroundColor: token.colorBgContainer,
      display: "flex",
      // height: screens.sm ? "100vh" : "auto",
      padding: screens.md ? `15px 25px` : "0px",
      textAlign: "left",
    },
    text: {
      color: token.colorTextSecondary,
    },
    title: {
      fontSize: screens.md ? token.fontSizeHeading2 : token.fontSizeHeading3,
    },
    button: {
      backgroundColor: isHover ? token.colorBgContainer : BrandColors.BUTTON_BG,
      borderColor: isHover ? BrandColors.BUTTON_BG : token.colorBgContainer,
      color: isHover ? BrandColors.BUTTON_BG : token.colorBgContainer,
      height: "42px",
      borderRadius: "50px",
      width: "100%",
      textTransform: "uppercase",
      fontWeight: "500",
      boxShadow: "none",
    },
    upload: {
      display: "flex",
      background: "#f00",
    },
    subheader: {
      fontWeight: "600",
      marginBottom: "8px",
      marginTop: "16px",
      display: "flex",
      width: "100%",
    },
    error: {
      color: "#f00",
      marginTop: "10px",
    },
    visitListTable: {
      width: "100%",
      border: "1px solid #dee2e6",
      borderCollapse: "collapse",
    },
    visitTblTh: {
      textAlign: "left",
      background: "#e9ecef",
      padding: "10px",
      border: "1px solid #dee2e6",
      borderCollapse: "collapse",
    },
    visitTblTd: {
      padding: "8px 10px",
      border: "1px solid #dee2e6",
      borderCollapse: "collapse",
    },
  };
  const uiState = useSelector((state: any) => state.portal.uiState);
  const preferredLanguage = useSelector(
    (state: any) => state.portal.uiState.Language
  );
  console.log(preferredLanguage,"preferredLanguage")
  const [isRightAlign, setIsRightAlign] = useState(false);

  useEffect(() => {    
    try {
      if (whitelabelConfig.uiSettings.rightAlignedLanguage.includes(preferredLanguage) === true) {
        setIsRightAlign(true);
      } else {
        setIsRightAlign(false);
      }
    } catch (e) {
      console.log("Error", e)
      handleErrorNavigation(e, navigate, t);
    }
  }, [ whitelabelConfig, preferredLanguage]);

  useEffect(() => {
    GetPatientMobileSettings()
      ?.then((res) => {
        console.log("mobilsetting", res.data);

        let payload = {
          toolCode: res?.data?.PatientHealthTools[0]?.HealthToolCode,
          patientHealthToolID:
            res?.data?.PatientHealthTools[0]?.PatientHealthToolID,
          registeredToolID:
            res?.data?.PatientHealthTools[0]?.RegisteredHealthToolID,
        };
        setPayload(payload);
        initChartData(payload);
      })
      .catch((e) => {
        handleErrorNavigation(e, navigate, t);
      });
  }, []);

  const initChartData = (payload: any) => {
    chartDesignConfig["chartDimensions"] = {
      height: 600,
      width: 350,
    };
    chartDesignConfig["chartDimensions"].height =
      chartDesignConfig["chartDimensions"].height < 350
        ? 350
        : chartDesignConfig["chartDimensions"].height;
    chartDesignConfig["weeksToDisplay"] = 7;
    chartDesignConfig["acceptableWeightDeviationThreshold"] = 0.25;
    chartData = {
      labels: [],
      datasets: [
        {
          label: t("PREGNANCY_WEIGHT_CALCULATOR.PROJECTED_ACTUAL"),
          borderColor: chartColors.yellow,
          data: [],
          pointBackgroundColor: [],
          pointBorderColor: [],
          pointHoverBackgroundColor: [],
          pointHoverBorderColor: [],
          pointRadius: 3.5,
          pointHoverRadius: 4.5,
        },
        {
          label: t("PREGNANCY_WEIGHT_CALCULATOR.PROJECTED_MAX"),
          borderColor: chartColors.blue,
          backgroundColor: chartColors.blue,
          data: [],
          fill: false,
          pointRadius: 0,
        },
        {
          label: t("PREGNANCY_WEIGHT_CALCULATOR.PROJECTED_MIN"),
          fill: "-1",
          backgroundColor: chartColors.blue,
          borderColor: chartColors.blue,
          data: [],
          pointRadius: 0,
        },
      ],
    };
    chartOptions = {
      legend: {
        display: false,
      },
      scales: {
        yAxes: [
          {
            display: true,
            scaleLabel: {
              display: true,
              labelString: t("PREGNANCY_WEIGHT_CALCULATOR.WEIGHT_IBS"),
            },
            ticks: {},
          },
        ],
      },
    };
    getPregnancyToolData(payload)?.then((pregres) => {
      setPregnancyData(pregres.data);

      var data = pregres.data;
      if (data.PregnancyWeightCalculatorToolDetails?.length) {
        data.PregnancyWeightCalculatorToolDetails.sort(function (a, b) {
          return a.PregnancyWeek - b.PregnancyWeek;
        });
        chartData.datasets[0].data =
          data.PregnancyWeightCalculatorToolDetails.map((e) =>
            (function () {
              return { x: e.PregnancyWeek, y: e.WeightLbs.toFixed(1) };
            })()
          );
      }
      renderChart(data);
    });
  };
  const calctarget = (t, w0, period1, r1max, ratecat, r1min) => {
    var wmax =
      t <= period1
        ? w0 + r1max * t
        : w0 + r1max * period1 + ratecat.r2max * (t - period1);
    var wmin =
      t <= period1
        ? w0 + r1min * t
        : w0 + r1min * period1 + ratecat.r2min * (t - period1);
    return {
      week: t,
      wmin: wmin.toFixed(2),
      wmax: wmax.toFixed(2),
    };
  };

  const projectBMI = (weight, heightFt, heightIn, hasTwins) => {
    var period1 = 13;
    var period2 = 39 - period1;
    var normaltarget = [
      {
        min: 28,
        max: 40,
      },
      {
        min: 25,
        max: 35,
      },
      {
        min: 15,
        max: 25,
      },
      {
        min: 11,
        max: 20,
      },
    ];
    var twintarget = [
      {
        min: 37,
        max: 54,
      },
      {
        min: 37,
        max: 54,
      },
      {
        min: 31,
        max: 50,
      },
      {
        min: 25,
        max: 42,
      },
    ];
    var target = hasTwins ? twintarget : normaltarget;

    var r1min = 1.1 / period1;
    var r1max = 4.4 / period1;

    var rate = [
      {
        BMI: 1,
        r2min: (target[0].min - 1.1) / period2,
        r2max: (target[0].max - 4.4) / period2,
        r2: 1.0,
      },
      {
        BMI: 2,
        r2min: (target[1].min - 1.1) / period2,
        r2max: (target[1].max - 4.4) / period2,
        r2: 1.0,
      },
      {
        BMI: 3,
        r2min: (target[2].min - 1.1) / period2,
        r2max: (target[2].max - 4.4) / period2,
        r2: 0.6,
      },
      {
        BMI: 4,
        r2min: (target[3].min - 1.1) / period2,
        r2max: (target[3].max - 4.4) / period2,
        r2: 0.5,
      },
    ];

    var BMIcategories = [
      {
        cat: 1,
        min: 0,
        max: 18.5,
      },
      {
        cat: 2,
        min: 18.5,
        max: 25,
      },
      {
        cat: 3,
        min: 25,
        max: 30,
      },
      {
        cat: 4,
        min: 30,
        max: 100,
      },
    ];

    var w0 = weight;
    var height = {
      ft: heightFt,
      in: heightIn,
    };

    var bmi =
      w0 / 2.2 / Math.pow(((height.ft * 12 + height.in) * 2.54) / 100, 2);
    console.log(w0);
    // var bmicat = BMIcategories.filter((b) => bmi >= b.min && bmi < b.max)[0].cat;
    // var ratecat = rate.filter((b) => b.BMI == bmicat)[0];
    var bmicatObj = BMIcategories.filter((b) => bmi >= b.min && bmi < b.max)[0];
    var bmicat = bmicatObj ? bmicatObj.cat : "Unknown";
    var ratecatObj = rate.filter((b) => b.BMI === bmicat)[0];
    var ratecat = ratecatObj || { r2max: 0, r2min: 0 };

    var weeks = fillArrayWithNumbers(40);
    weeks = weeks.map((w) => calctarget(w, w0, period1, r1max, ratecat, r1min));
    chartData.datasets[1].data = weeks.map((w) =>
      (function () {
        return { x: w.week, y: w.wmax };
      })()
    );
    chartData.datasets[2].data = weeks.map((w) =>
      (function () {
        return { x: w.week, y: w.wmin };
      })()
    );
  };
  const fillArrayWithNumbers = (n) => {
    var arr = Array.apply(null, Array(n));
    return arr.map(function (x, i) {
      return i;
    });
  };
  const renderChart = (data: any) => {
    console.log(data);
    projectBMI(
      data.PrePregnancyWeight,
      data.HeightFeet,
      data.HeightInch,
      data.IsTwins
    );
    chartOptions.scales.yAxes[0].ticks.min =
      Math.min.apply(
        null,
        chartData.datasets[2].data.map((e) => e.y)
      ) - 10;
    chartOptions.scales.yAxes[0].ticks.max =
      Math.max.apply(
        null,
        chartData.datasets[1].data.map((e) => e.y)
      ) + 10;
    chartData.labels = [
      0, 1, 2, 3, 4, 5, 6, 7
    ];
    updatePregnancyPointColors();
  };
  const updatePregnancyPointColors = () => {
    var textStyle = 2;
    var targetDataset = chartData.datasets[0];
    var minData = chartData.datasets[2].data;
    var maxData = chartData.datasets[1].data;
    var isWeightNormal;
    chartData.datasets[0].data.forEach((e) => {
      isWeightNormal =
        (e.y >= minData[e.x].y ||
          minData[e.x].y - e.y <=
          chartDesignConfig["acceptableWeightDeviationThreshold"]) &&
        (e.y <= maxData[e.x].y ||
          e.y - maxData[e.x].y <=
          chartDesignConfig["acceptableWeightDeviationThreshold"]);
      targetDataset.pointBackgroundColor.push(
        isWeightNormal ? chartColors.yellow : chartColors.red
      );
      targetDataset.pointBorderColor.push(
        isWeightNormal ? chartColors.yellow : chartColors.red
      );
      targetDataset.pointHoverBackgroundColor.push(
        isWeightNormal ? chartColors.yellow : chartColors.red
      );
      targetDataset.pointHoverBorderColor.push(
        isWeightNormal ? chartColors.yellow : chartColors.red
      );
    });
    chartOptions = {
      legend: {
        display: false,
        labels: {
          fontSize: textStyle * 6,
        },
      },
      hover: {
        mode: "nearest",
        intersect: true,
      },
      elements: {
        line: {
          tension: 0,
        },
      },
      scales: {
        xAxes: [
          {
            display: true,
            scaleLabel: {
              display: false,
              labelString: t("PREGNANCY_WEIGHT_CALCULATOR.WEEKS"),
            },
            ticks: {
              min: 0,
              max: chartDesignConfig["weeksToDisplay"],
              fontSize: textStyle * 6,
            },
          },
        ],
        yAxes: [
          {
            display: true,
            scaleLabel: {
              display: true,
              labelString: t("PREGNANCY_WEIGHT_CALCULATOR.WEIGHT_IBS"),
            },
            ticks: { fontSize: textStyle * 6 },
          },
        ],
      },
      responsive: true,
      maintainAspectRatio: false,
    };
  };

  const updateForm = async () => {
    const payload = {
      toolCode: healthPayload?.toolCode,
      patientHealthToolID: healthPayload?.patientHealthToolID,
      registeredToolID: healthPayload?.registeredToolID,
      inputData: {
        PregnancyWeightCalculatorToolID: healthPayload?.registeredToolID,
        PregnancyWeek: weeksPregnant,
        WeightLbs: currentWeight,
      },
    };
    UpdatePregnancyWeight(payload)
      ?.then((res) => {
        if (res.data.ResponseStatus == 0) {
          UseToaster({
            type: "success",
            message: res.data.ResponseMessage,
            description: "",
          });
          initChartData(healthPayload)
        }
      })
      .catch((e) => { });
  };
  return (
    <div>
      <Card style={{ padding: "0px", margin: "0px", borderRadius: "0px" }}>
        {pregnancyData ? <Card className="mb-3" style={{ marginBottom: "15px" }}><PregnancyWeightChart rawData={pregnancyData} /></Card> : ""}
        <div className="row">
          <div className="col-sm-12">
            <Card title={t("PREGNANCY_WEIGHT_CALCULATOR.PERSONAL_INFORMATION")} style={{ textAlign: isRightAlign ? "right" : "left" }} className="mb-3" >
              <Form form={form} layout="vertical" onFinish={updateForm} className={isRightAlign ? "right_align_text" : ""}>
                <div>
                  <Form.Item
                    label={t("PREGNANCY_WEIGHT_CALCULATOR.WEEKS_PREGNANT")}
                    name="weeksPregnant"
                    style={{ fontWeight: "600" }}                    
                    rules={[
                      {
                        required: true,
                        message: t("PREGNANCY_WEIGHT_CALCULATOR.INPUT_YOUR_WEEKSPREGNANT"),
                      },
                    ]}
                  >
                    <Select style={{ marginTop: "7px", height: "40px" }}
                      value={weeksPregnant}
                      onChange={(value) => setWeeksPregnant(value)}
                    >
                      {[...Array(9).keys()].map((week) => (
                        <Option key={week + 1} value={week + 1}>
                          {week + 1}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                <Form.Item
                  label={t("PREGNANCY_WEIGHT_CALCULATOR.CURRENT_WEIGHT")}
                  name="currentWeight"
                  style={{ fontWeight: "600" }}
                  rules={[
                    { required: true, message: t("PREGNANCY_WEIGHT_CALCULATOR.INPUT_YOUR_WEIGHT") },
                  ]}
                >
                  <Input
                    type="number"
                    value={currentWeight}
                    onChange={(e) => setCurrentWeight(e.target.value)}
                    addonAfter="lbs"
                    style={{ marginTop: "7px", height: "40px" }}
                  />
                </Form.Item>

                <Form.Item
                  label={t("PREGNANCY_WEIGHT_CALCULATOR.CARRYING_TWINS")}
                  name="carryingTwins"
                  style={{ fontWeight: "600" }}
                >
                  <p style={{ fontWeight: "400",textAlign: isRightAlign ? "right" : "left" }}> {pregnancyData?.IsTwins ? "Yes" : "No"}</p>
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={styles.button}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    {t("PREGNANCY_WEIGHT_CALCULATOR.UPDATE")}
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PregnancyWeightCalculator;